/**
 * Inline Widget Registry
 *
 * Unified registry for inline widget types and mentionable models.
 * Domains register their widgets and mentionable models here via portal.ts.
 * Mirrors the componentRegistry.ts pattern from @ja/crud.
 *
 * Widget Types:
 * - 'mention': People/record tagging (built-in)
 * - 'contact': Inline contact card (built-in)
 * - '{domain}-{name}': Domain-specific widgets
 *
 * @example
 * // In Domain portal.ts:
 * import { registerWidget, registerMentionableModel } from '@ja/core-ui/composites/inline-widgets/widgetRegistry'
 *
 * registerWidget('inspection-status', () => import('./widgets/InspectionStatusBadge.vue'), {
 *   package: 'forms',
 *   description: 'Inspection completion status',
 *   exampleSyntax: '{{inspection-status:inspection:uuid}}',
 *   icon: 'ClipboardCheck',
 * })
 *
 * registerMentionableModel({
 *   model: 'client', labelField: 'name', searchField: 'name',
 *   icon: 'Building2', package: 'billing',
 * })
 */

import { shallowReactive, type AsyncComponentLoader, type Component } from 'vue'

// ============================================================================
// Types
// ============================================================================

export type ComponentModule = { default: Component }
export type AsyncComponent = AsyncComponentLoader<Component>
export type ComponentEntry = ComponentModule | AsyncComponent

export interface WidgetRegistration {
  /** Vue component or async loader for rendering this widget */
  component: ComponentEntry
  /** Domain/package that registered this widget */
  package: string
  /** Human-readable description (shown in hints button) */
  description: string
  /** Example syntax (shown in hints button) */
  exampleSyntax: string
  /** Trigger character in editor (e.g., '@' for mentions) */
  triggerChar?: string
  /** Lucide icon name (shown in hints button) */
  icon?: string
}

export interface MentionableModelConfig {
  /** CRUD model key (e.g., 'user', 'client') */
  model: string
  /** Field to display as label (e.g., 'full_name') */
  labelField: string
  /** Field to search on (e.g., 'full_name') */
  searchField: string
  /** Lucide icon name for the result list */
  icon?: string
  /** Domain/package that registered this */
  package: string
}

// ============================================================================
// Registry State
// ============================================================================

/** Map: widgetType -> WidgetRegistration */
const widgetRegistry = shallowReactive(new Map<string, WidgetRegistration>())

/** Map: model -> MentionableModelConfig */
const mentionableModels = shallowReactive(new Map<string, MentionableModelConfig>())

// ============================================================================
// Widget Registry API
// ============================================================================

/**
 * Register an inline widget type.
 *
 * @param widgetType - Unique identifier (e.g., 'mention', 'contact', 'fieldservice-task-status')
 * @param component - Vue component or async component loader
 * @param options - Registration metadata
 */
export function registerWidget(
  widgetType: string,
  component: ComponentEntry,
  options: Omit<WidgetRegistration, 'component'>,
): void {
  if (widgetRegistry.has(widgetType)) {
    const existing = widgetRegistry.get(widgetType)
    console.warn(
      `[widgetRegistry] Widget '${widgetType}' already registered by '${existing?.package}'. ` +
      `Overwriting with '${options.package}'.`,
    )
  }

  widgetRegistry.set(widgetType, { component, ...options })

  if (import.meta.env.DEV) {
    console.log(`[widgetRegistry] Registered widget '${widgetType}' from '${options.package}'`)
  }
}

/**
 * Get a registered widget.
 */
export function getWidget(widgetType: string): WidgetRegistration | undefined {
  return widgetRegistry.get(widgetType)
}

/**
 * Get the component for a widget type.
 */
export function getWidgetComponent(widgetType: string): ComponentEntry | undefined {
  return widgetRegistry.get(widgetType)?.component
}

/**
 * Check if a widget type is registered.
 */
export function hasWidget(widgetType: string): boolean {
  return widgetRegistry.has(widgetType)
}

/**
 * Get all registered widgets.
 */
export function getAllWidgets(): Map<string, WidgetRegistration> {
  return widgetRegistry
}

/**
 * Unregister a widget (useful for testing).
 */
export function unregisterWidget(widgetType: string): boolean {
  return widgetRegistry.delete(widgetType)
}

/**
 * Clear all widget registrations (useful for testing).
 */
export function clearWidgetRegistry(): void {
  widgetRegistry.clear()
}

// ============================================================================
// Mentionable Models API
// ============================================================================

/**
 * Register a model as mentionable (appears in @ autocomplete).
 *
 * @param config - Model configuration for mention search
 */
export function registerMentionableModel(config: MentionableModelConfig): void {
  if (mentionableModels.has(config.model)) {
    const existing = mentionableModels.get(config.model)
    console.warn(
      `[widgetRegistry] Mentionable model '${config.model}' already registered by '${existing?.package}'. ` +
      `Overwriting with '${config.package}'.`,
    )
  }

  mentionableModels.set(config.model, config)

  if (import.meta.env.DEV) {
    console.log(`[widgetRegistry] Registered mentionable model '${config.model}' from '${config.package}'`)
  }
}

/**
 * Get all registered mentionable models.
 */
export function getMentionableModels(): MentionableModelConfig[] {
  return Array.from(mentionableModels.values())
}

/**
 * Check if a model is registered as mentionable.
 */
export function hasMentionableModel(model: string): boolean {
  return mentionableModels.has(model)
}

/**
 * Unregister a mentionable model (useful for testing).
 */
export function unregisterMentionableModel(model: string): boolean {
  return mentionableModels.delete(model)
}

/**
 * Clear all mentionable model registrations (useful for testing).
 */
export function clearMentionableModels(): void {
  mentionableModels.clear()
}

// ============================================================================
// Contactable Models API
// ============================================================================

/** Map: model -> MentionableModelConfig (reuses same config shape) */
const contactableModels = shallowReactive(new Map<string, MentionableModelConfig>())

/**
 * Register a model as contactable (appears in {{contact: autocomplete).
 * Contactable models are those with phone/email contact methods (Contactable trait).
 */
export function registerContactableModel(config: MentionableModelConfig): void {
  if (contactableModels.has(config.model)) {
    const existing = contactableModels.get(config.model)
    console.warn(
      `[widgetRegistry] Contactable model '${config.model}' already registered by '${existing?.package}'. ` +
      `Overwriting with '${config.package}'.`,
    )
  }

  contactableModels.set(config.model, config)

  if (import.meta.env.DEV) {
    console.log(`[widgetRegistry] Registered contactable model '${config.model}' from '${config.package}'`)
  }
}

/**
 * Get all registered contactable models.
 */
export function getContactableModels(): MentionableModelConfig[] {
  return Array.from(contactableModels.values())
}

/**
 * Clear all contactable model registrations (useful for testing).
 */
export function clearContactableModels(): void {
  contactableModels.clear()
}
